import React, { useState } from 'react';
import { LoginApi, AdminApi, BfiApi } from '@apply/api';
import { applicationInsights } from '../ApplicationInsights';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { handleAdminInfo } from '../common/functions/handleAdminInfo';
import { handleAdminSuccess } from '../common/functions/handleAdminSuccess';
import { LoadPanel } from 'devextreme-react/load-panel';
import { handleAdminError } from '../common/functions/handleAdminError';
import { PhoneInput, isPhoneNumberValid } from '@apply/ui';

export const AdminSettings = () => {
    const adminApi = new AdminApi.AdminApi(applicationInsights);
    const loginApi = new LoginApi.LoginApi(applicationInsights);

    const [firstName, set_firstName] = useState<string>();
    const [lastName, set_lastName] = useState<string>();
    const [email, set_email] = useState<string>();
    const [homePhone, set_homePhone] = useState<string>();
    const [workPhone, set_workPhone] = useState<string>();
    const [mobilePhone, set_mobilePhone] = useState<string>();
    const [title, set_title] = useState<string>();
    const [password, set_password] = useState<string>('');
    const [newPassword, set_newPassword] = useState<string>('');
    const [confirmPassword, set_confirmPassword] = useState<string>('');
    const [passwordResetResult, setPasswordResetResult] = useState<BfiApi.IChangePasswordResult>();
    const [loadingMessage, set_loadingMessage] = useState('');

    React.useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            set_loadingMessage('Loading...');

            const response = await adminApi.getSettings();
            if (response.statusCode !== 200) {
                return;
            }

            const adminSettings = response.result;
            set_firstName(adminSettings?.firstName);
            set_lastName(adminSettings?.lastName);
            set_email(adminSettings.email);
            set_homePhone(adminSettings?.homePhone);
            set_workPhone(adminSettings?.workPhone);
            set_title(adminSettings?.title);
            set_mobilePhone(adminSettings?.mobilePhone);
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred. Please try again');
        } finally {
            set_loadingMessage('');
        }
    };

    const _onUserSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        try {
            set_loadingMessage('Saving...');
            const model = {
                firstName,
                lastName,
                email,
                homePhone,
                workPhone,
                mobilePhone,
                title,
            };

            setPasswordResetResult(undefined);
            await adminApi.updateSettings(model);
            handleAdminSuccess('User information saved!');
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error occurred. Please try again');
        } finally {
            set_loadingMessage('');
        }
    };

    const _onPasswordSubmit = async (event: React.FormEvent<Element>) => {
        event.preventDefault();

        setPasswordResetResult(undefined);

        if (!password) {
            handleAdminInfo('Enter a valid password');
            return;
        }

        try {
            set_loadingMessage('Updating password...');

            const response = await loginApi.updatePassword({
                password,
                newPassword,
            });
            if (response.statusCode === 200) {
                if (response.result) {
                    const result = response.result as BfiApi.IApiChangePasswordResponse;
                    if (result.result.succeeded) {
                        set_password('');
                        set_newPassword('');
                        set_confirmPassword('');
                        handleAdminSuccess('Password updated!');
                    } else {
                        if (result.errors && result.errors.length > 0) {
                            throw new Error(`Encountered an error - ${result.errors}`);
                        } else {
                            //Some kind of password policy failure occurred...
                            setPasswordResetResult(result.result);
                        }
                    }
                } else {
                    throw new Error('An unexpected error occurred. Please try again.');
                }
            } else {
                if (response.result) {
                    const result = response.result;
                    if (result.errors && result.errors.length > 0) {
                        set_snackBarErrorMessage(`Encountered an error - ${result.errors}`);
                        set_snackBarErrorOpen(true);
                    } else {
                        //Some kind of password policy failure occurred...
                        set_snackBarErrorMessage('An unexpected error has occured');
                        set_snackBarErrorOpen(true);
                    }
                }
            }
        } catch (ex) {
            handleAdminError(ex, ex.message);
        } finally {
            set_loadingMessage('');
        }
    };

    const styles = {
        submitButton: {
            marginLeft: '20px',
        },
        fullWidth: {
            width: '100%',
        },
        formBottomMargin: {
            marginBottom: 50,
        },
        topDisclaimer: {
            color: '#AAA',
            'font-weight': 100,
            fontSize: '1rem',
        },
    };

    const canSave = () => {
        return (
            firstName &&
            lastName &&
            email &&
            title &&
            isPhoneNumberValid(homePhone, false) &&
            isPhoneNumberValid(workPhone, false) &&
            isPhoneNumberValid(mobilePhone, false)
        );
    };

    return (
        <>
            <div
                className='page-header'
                role='banner'
            >
                <h1>Account Settings</h1>
            </div>
            <div
                className='main-content no-search'
                style={{ padding: 0, backgroundColor: '#f4f4f4' }}
            >
                <div className='scrollable-content'>
                    <div className='standard-form'>
                        <form
                            name='userInformationForm'
                            style={styles.formBottomMargin}
                            onSubmit={(event) => _onUserSubmit(event)}
                        >
                            <h2 className='decorated'>User Information</h2>
                            <div className='row'>
                                <div className='columns small-7 medium-6 large-3'>
                                    <label htmlFor='firstName'>
                                        First Name
                                        <span className='req'>*</span>
                                    </label>
                                    <input
                                        id='firstName'
                                        type='text'
                                        maxLength={100}
                                        value={firstName}
                                        onChange={(event) => set_firstName(event.target.value)}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>Your first name (e.g. John)</span>
                                    </span>
                                </div>
                                <div className='columns small-7 medium-6 large-3'>
                                    <label htmlFor='lastName'>
                                        Last Name
                                        <span className='req'>*</span>
                                    </label>
                                    <input
                                        id='lastName'
                                        type='text'
                                        maxLength={100}
                                        value={lastName}
                                        onChange={(event) => set_lastName(event.target.value)}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>Your last name (e.g. Smith)</span>
                                    </span>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='columns small-7 medium-6 large-3'>
                                    <label htmlFor='title'>
                                        Title
                                        <span className='req'>*</span>
                                    </label>
                                    <input
                                        id='title'
                                        type='text'
                                        maxLength={100}
                                        value={title}
                                        onChange={(event) => set_title(event.target.value)}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>Your title at BFI</span>
                                    </span>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='columns small-7 medium-6 large-6'>
                                    <label htmlFor='email'>
                                        Email <span className='req'>*</span> (also used as Login for the account)
                                    </label>
                                    <input
                                        id='email'
                                        type='email'
                                        value={email}
                                        onChange={(event) => set_email(event.target.value)}
                                        required={true}
                                        maxLength={320}
                                    />
                                    <div
                                        style={{
                                            color: 'darkred',
                                            fontSize: 'small',
                                        }}
                                    >
                                        * Changing this will also change the login for your account!
                                    </div>
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>Your business email address</span>
                                    </span>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='columns small-7 medium-6 large-2'>
                                    <label htmlFor='homePhone'>Home Phone</label>
                                    <PhoneInput
                                        id='homePhone'
                                        value={homePhone}
                                        onChange={(value) => set_homePhone(value)}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>e.g. (212)-555-1212</span>
                                    </span>
                                </div>
                                <div className='columns small-7 medium-6 large-2'>
                                    <label htmlFor='workPhone'>Work Phone</label>
                                    <PhoneInput
                                        id='workPhone'
                                        value={workPhone}
                                        onChange={(value) => set_workPhone(value)}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>e.g. (212)-555-1212</span>
                                    </span>
                                </div>
                                <div className='columns small-7 medium-6 large-2'>
                                    <label htmlFor='mobilePhone'>Mobile Phone</label>
                                    <PhoneInput
                                        id='mobilePhone'
                                        value={mobilePhone}
                                        onChange={(value) => set_mobilePhone(value)}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>e.g. (212)-555-1212</span>
                                    </span>
                                </div>
                                <div
                                    className='button-footer'
                                    style={styles.fullWidth}
                                >
                                    <button
                                        style={styles.submitButton}
                                        type='submit'
                                        className='button primary'
                                        disabled={!canSave()}
                                    >
                                        Update Information
                                    </button>
                                </div>
                            </div>
                        </form>
                        <h2 className='decorated'>Update Password</h2>
                        <form onSubmit={_onPasswordSubmit}>
                            <div className='row'>
                                <div className='columns small-12 large-3'>
                                    <label htmlFor='oldPassword'>
                                        Old Password
                                        <span className='req'>*</span>
                                    </label>
                                    <input
                                        id='password'
                                        type='password'
                                        value={password}
                                        required
                                        onChange={(event) => set_password(event.target.value)}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>Enter old password</span>
                                    </span>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='columns small-12 large-3'>
                                    <label htmlFor='passwordNew'>
                                        New Password
                                        <span className='req'>*</span>
                                    </label>
                                    <input
                                        id='passwordNew'
                                        type='password'
                                        value={newPassword}
                                        required
                                        onChange={(event) => set_newPassword(event.target.value)}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text text-wide'>
                                            Enter your new password. It must meet the following criteria:
                                            <br />
                                            <ul>
                                                <li>At least 8 characters</li>
                                                <li>A mixture of both uppercase and lowercase letters</li>
                                                <li>A mixture of letters and numbers</li>
                                                <li>Inclusion of at least one special character, e.g., ! @ # ? ]</li>
                                            </ul>
                                        </span>
                                    </span>
                                </div>
                            </div>
                            <div className='row'>
                                <div className='columns small-12 large-3'>
                                    <label htmlFor='passwordConfirm'>
                                        Confirm Password
                                        <span className='req'>*</span>
                                    </label>
                                    <input
                                        id='passwordConfirm'
                                        type='password'
                                        value={confirmPassword}
                                        required
                                        onChange={(event) => set_confirmPassword(event.target.value)}
                                    />
                                    <span className='helper'>
                                        <span className='tooltip-trigger'>
                                            <FontAwesomeIcon icon={faCircleQuestion} />
                                        </span>
                                        <span className='text'>Confirm new password</span>
                                    </span>
                                </div>
                            </div>
                            <div className='row'>
                                {passwordResetResult && (
                                    <div className='row'>
                                        <div
                                            className='columns small-12 large-3'
                                            style={{ fontSize: '12px' }}
                                        >
                                            Your password failed to meet the requirements indicated in{' '}
                                            <span style={{ color: 'red' }}>red</span> below...
                                            <ul>
                                                <li
                                                    style={{
                                                        color: passwordResetResult.passwordTooShort ? 'red' : 'inherit',
                                                    }}
                                                >
                                                    At least 8 characters
                                                </li>
                                                <li
                                                    style={{
                                                        color: passwordResetResult.passwordRequiresMixedCase ? 'red' : 'inherit',
                                                    }}
                                                >
                                                    A mixture of both uppercase and lowercase letters
                                                </li>
                                                <li
                                                    style={{
                                                        color: passwordResetResult.passwordRequiresDigit ? 'red' : 'inherit',
                                                    }}
                                                >
                                                    A mixture of letters and numbers
                                                </li>
                                                <li
                                                    style={{
                                                        color: passwordResetResult.passwordRequiresSpecialCharacter
                                                            ? 'red'
                                                            : 'inherit',
                                                    }}
                                                >
                                                    Inclusion of at least one special character, e.g., ! @ # ? ]
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                )}
                                <div
                                    className='button-footer'
                                    style={styles.fullWidth}
                                >
                                    <button
                                        disabled={
                                            password.length < 3 ||
                                            confirmPassword.length < 3 ||
                                            newPassword.length < 3 ||
                                            confirmPassword !== newPassword
                                        }
                                        style={styles.submitButton}
                                        type='submit'
                                        className='button primary'
                                    >
                                        Update Password
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={!!loadingMessage}
                message={loadingMessage}
            />
        </>
    );
};
